import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { adminApi } from "../../services/adminApi";

export default function Vehicles() {
  const [vehicles, setVehicles] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ cabNumber: "", make: "", model: "", year: "", vin: "", licensePlate: "", color: "", assignedDriverId: "", notes: "" });
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState("");

  async function load(q = "") {
    setLoading(true);
    try {
      const [v, d] = await Promise.all([
        adminApi.listVehicles(q ? { search: q } : {}),
        adminApi.listDrivers(),
      ]);
      setVehicles(v.vehicles);
      setDrivers(d.drivers);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }

  useEffect(() => { load(); }, []);

  async function handleCreate(e) {
    e.preventDefault();
    setCreateError("");
    setCreating(true);
    try {
      const payload = { ...form, year: Number(form.year) };
      if (!payload.assignedDriverId) delete payload.assignedDriverId;
      await adminApi.createVehicle(payload);
      setShowCreate(false);
      setForm({ cabNumber: "", make: "", model: "", year: "", vin: "", licensePlate: "", color: "", assignedDriverId: "", notes: "" });
      load();
    } catch (err) { setCreateError(err.message); }
    finally { setCreating(false); }
  }

  const driverMap = new Map(drivers.map((d) => [d._id, `${d.firstName} ${d.lastName}`]));

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Vehicles</h1>
        <button onClick={() => setShowCreate(true)}
          className="bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold px-4 py-2 rounded-lg transition flex items-center gap-2">
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" /></svg>
          Add Vehicle
        </button>
      </div>

      <input type="text" placeholder="Search cab #, vehicles, plate, VIN..." value={search}
        onChange={(e) => { setSearch(e.target.value); load(e.target.value); }}
        className="mb-4 w-full max-w-sm border border-gray-300 rounded-lg px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />

      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        {loading ? <div className="p-8 text-center text-gray-400">Loading…</div> :
          vehicles.length === 0 ? <div className="p-8 text-center text-gray-400">No vehicles found.</div> : (
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-4 py-3 text-left font-medium text-gray-600">Cab #</th>
                  <th className="px-4 py-3 text-left font-medium text-gray-600">Vehicle</th>
                  <th className="px-4 py-3 text-left font-medium text-gray-600">Plate</th>
                  <th className="px-4 py-3 text-left font-medium text-gray-600">VIN</th>
                  <th className="px-4 py-3 text-left font-medium text-gray-600">Assigned Driver</th>
                  <th className="px-4 py-3 text-left font-medium text-gray-600">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {vehicles.map((v) => (
                  <tr key={v._id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 font-semibold text-gray-900">{v.cabNumber || "—"}</td>
                    <td className="px-4 py-3">
                      <Link to={`/enrollme/admin/vehicles/${v._id}`} className="font-medium text-blue-600 hover:underline">
                        {v.year} {v.make} {v.model}
                      </Link>
                    </td>
                    <td className="px-4 py-3 text-gray-600">{v.licensePlate}</td>
                    <td className="px-4 py-3 text-gray-500 text-xs">{v.vin || "—"}</td>
                    <td className="px-4 py-3 text-gray-600">{v.assignedDriverId ? driverMap.get(v.assignedDriverId) || "—" : "—"}</td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${v.status === "active" ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-500"}`}>{v.status}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
      </div>

      {showCreate && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6 max-h-screen overflow-y-auto">
            <h2 className="text-lg font-bold text-gray-900 mb-4">Add Vehicle</h2>
            {createError && <div className="bg-red-50 text-red-700 border border-red-200 rounded-lg px-4 py-2 mb-3 text-sm">{createError}</div>}
            <form onSubmit={handleCreate} className="space-y-3">
              <div>
                <label className="text-xs font-medium text-gray-600">Cab # *</label>
                <input
                  required
                  inputMode="numeric"
                  pattern="\d{1,5}"
                  maxLength={5}
                  value={form.cabNumber}
                  onChange={(e) => setForm({ ...form, cabNumber: e.target.value.replace(/\D/g, "").slice(0, 5) })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm mt-1 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="123"
                />
                <p className="text-xs text-gray-400 mt-1">Cab # must be 1–5 digits and unique.</p>
              </div>
              <div className="grid grid-cols-2 gap-3">
                {[["Make", "make", true], ["Model", "model", true], ["Year", "year", true], ["Color", "color", false]].map(([label, key, req]) => (
                  <div key={key}>
                    <label className="text-xs font-medium text-gray-600">{label}{req && " *"}</label>
                    <input required={req} value={form[key]} onChange={(e) => setForm({ ...form, [key]: e.target.value })}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm mt-1 focus:outline-none focus:ring-2 focus:ring-blue-500" />
                  </div>
                ))}
              </div>
              <div>
                <label className="text-xs font-medium text-gray-600">License Plate *</label>
                <input required value={form.licensePlate} onChange={(e) => setForm({ ...form, licensePlate: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm mt-1 focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
              <div>
                <label className="text-xs font-medium text-gray-600">VIN</label>
                <input value={form.vin} onChange={(e) => setForm({ ...form, vin: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm mt-1 focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
              <div>
                <label className="text-xs font-medium text-gray-600">Assign to Driver (optional)</label>
                <select value={form.assignedDriverId} onChange={(e) => setForm({ ...form, assignedDriverId: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm mt-1 focus:outline-none focus:ring-2 focus:ring-blue-500">
                  <option value="">— Not assigned —</option>
                  {drivers.map((d) => <option key={d._id} value={d._id}>{d.firstName} {d.lastName}</option>)}
                </select>
              </div>
              <div className="flex gap-3 pt-2">
                <button type="submit" disabled={creating}
                  className="flex-1 bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2.5 rounded-lg text-sm transition disabled:opacity-60">
                  {creating ? "Adding…" : "Add Vehicle"}
                </button>
                <button type="button" onClick={() => setShowCreate(false)}
                  className="px-5 py-2.5 border border-gray-300 rounded-lg text-sm text-gray-600 hover:bg-gray-50">Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
